import React, { Fragment, ReactElement, useCallback, useMemo } from 'react';
import { Transition, Dialog } from '@headlessui/react';
import dayjs from 'dayjs';
import { XMarkIcon } from '@heroicons/react/20/solid';

import { Button, ReactTable } from 'src/ui/components';

import useChangeHistoryColumns from './useChangeHistoryColumns';

import { PersonHistorytype } from '../api/useGetPersonAuditLogs';

import {
  flattenNewValueWithLabelsAddress,
  flattenObject,
  flattenPhoneDetails,
  formatDelimitedString,
  tryParse,
} from '../utils/changeHistoryhelper';

import { PersonRepoType } from '../types/personRepoListTypes';
import { table } from 'console';

type ColumnsSearchType = {
  personalDetailCategory?: string;
  previousValue?: string;
  currentValue?: string;
};

type CurrentSortItemType = {
  sortKey: string;
  sortOrder: string;
};

type changeHistoryDataType = {
  pageParams: Array<any>;
  pages: Array<PersonHistorytype>;
};

type ChangeHistoryModalProps = {
  isChangeHistoryModalOpen: boolean;
  setIsChangeHistoryModalOpen: (val: boolean) => void;
  selectedPerson: PersonRepoType | undefined;
  changeHistoryData: changeHistoryDataType | undefined;
  currentSortItem: CurrentSortItemType;
  setCurrentSortItem: (item: { sortKey: string; sortOrder: string }) => void;
  columnsSearch: ColumnsSearchType;
  setColumnsSearch: (item: ColumnsSearchType) => void;
  resetChangeHistoryFilters: () => void;
  isChangeHistoryLoading: boolean;
  onEndReached: () => void;
  lastUpdated: string | undefined;
  refetchChangeHistoryList: () => void;
};

export function ChangeHistoryModal({
  isChangeHistoryModalOpen,
  setIsChangeHistoryModalOpen,
  selectedPerson,
  changeHistoryData,
  currentSortItem,
  setCurrentSortItem,
  columnsSearch,
  setColumnsSearch,
  resetChangeHistoryFilters,
  isChangeHistoryLoading,
  onEndReached,
  lastUpdated,
  refetchChangeHistoryList,
}: ChangeHistoryModalProps): ReactElement {
  const tableData = useMemo(() => {
    if (!changeHistoryData?.pages) return [];

    return changeHistoryData.pages
      .map((page: any) =>
        page.data.map((row: any) => {
          let newValue = row.NewValue;
          let previousValue = row.PreviousValue ?? '';

          if (row.ColumnName === 'PrimaryAddress') {
            newValue = flattenNewValueWithLabelsAddress(row.NewValue);
            previousValue = row.PreviousValue
              ? flattenNewValueWithLabelsAddress(row.PreviousValue)
              : '';
          } else if (
            row.ColumnName === 'PrimaryPhone' ||
            row.ColumnName === 'SecondaryPhone'
          ) {
            newValue = flattenPhoneDetails(row.NewValue);
            previousValue = row.PreviousValue
              ? flattenPhoneDetails(row.PreviousValue)
              : '';
          } else {
            newValue = formatDelimitedString(flattenObject(tryParse(newValue)));
            previousValue = formatDelimitedString(
              flattenObject(tryParse(previousValue)),
            );
          }

          return {
            ...row,
            NewValue: newValue,
            PreviousValue: previousValue,
          };
        }),
      )
      .flat();
  }, [changeHistoryData]);

  const columns = useChangeHistoryColumns();

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch, setColumnsSearch],
  );

  return (
    <Transition show={isChangeHistoryModalOpen} appear as={Fragment}>
      <Dialog onClose={() => setIsChangeHistoryModalOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="pr-dialog__backdrop" />
        </Transition.Child>
        <div className="pr-dialog__wrapper">
          <div className="pr-dialog__panel__container">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="pr-dialog__panel flex h-[calc(100svh-110px)] max-h-[calc(100svh-110px)] flex-col sm:!max-w-screen-2xl">
                <div className="flex items-center gap-3 px-4 py-3">
                  <Dialog.Title
                    as="h3"
                    className="grow truncate text-lg font-medium text-gray-900 dark:text-white"
                  >
                    Change log
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={() => setIsChangeHistoryModalOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="flex items-start justify-between border-t border-light-light px-5 py-2 dark:border-dark-medium">
                  <div>
                    <span className="mr-1 inline-block text-sm">
                      Log last refreshed at{' '}
                      <strong>
                        {' '}
                        {lastUpdated ? lastUpdated : dayjs().format('HH:mm:ss')}
                      </strong>{' '}
                      Please Refresh for the latest changes.
                    </span>{' '}
                    <Button
                      size="small"
                      variant="link"
                      className="px-0"
                      onClick={() => refetchChangeHistoryList()}
                    >
                      Refresh list
                    </Button>
                  </div>
                  <Button
                    size="small"
                    className="shrink-0"
                    onClick={() => resetChangeHistoryFilters()}
                  >
                    Reset filters
                  </Button>
                </div>
                <div className="relative flex min-h-0 grow flex-col overflow-auto scroll-smooth text-sm">
                  <ReactTable
                    columns={columns}
                    data={tableData}
                    handleSortChange={handleSortChange}
                    sortedItem={currentSortItem}
                    headerUpdateCallBack={headerUpdateCallBack}
                    columnsSearch={columnsSearch}
                    isLoading={isChangeHistoryLoading}
                    onEndReached={onEndReached}
                    className={`h-full max-w-full overflow-y-auto ${
                      tableData?.length || isChangeHistoryLoading
                        ? '[&>table]:!h-auto'
                        : '[&>table]:!h-[40%]'
                    }`}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
